import { useEffect, useState } from "react";
import { format } from "date-fns";
import { Calendar as CalendarIcon, Check } from "lucide-react";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { createReservation, listSiteContent } from "@/lib/api";
import HeroSlideshow from "@/components/HeroSlideshow";
import { useSwipeToNavigate } from "@/components/MobilePageDots";

const TIMES = [
  "18:00", "18:30", "19:00", "19:30", "20:00", "20:30", "21:00", "21:30",
];

const PARTY = ["1", "2", "3", "4", "5", "6", "7", "8"];

const OCCASIONS = [
  "None", "Anniversary", "Birthday", "Business Dinner", "Date Night", "Celebration",
];

const DEFAULT_HERO = {
  overline: "Reservation",
  title_top: "A table,",
  title_italic: "held for you.",
  body:
    "Dinner service Tuesday to Sunday. Larger parties, private events and press enquiries — please note in the field below and we'll respond within 24 hours.",
  reservation_notes_label: "01 — Notes",
  reservation_notes_body:
    "Reservations are held for 15 minutes past the booked time. For parties of 8 or more, please contact us directly.",
  reservation_dress_label: "02 — Dress",
  reservation_dress_body:
    "Smart casual. We ask that guests set aside 2 hours to enjoy the full experience.",
  reservation_enquiries_label: "03 — Enquiries",
  reservation_phone: "+62 21 1234 5678",
};

export default function Reservation() {
  const [date, setDate] = useState();
  const [time, setTime] = useState("");
  const [party, setParty] = useState("");
  const [occasion, setOccasion] = useState("");
  const [hero, setHero] = useState(DEFAULT_HERO);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const items = await listSiteContent();
        if (cancelled) return;
        const h = items.find((c) => c.section === "reservation-hero");
        if (h) setHero({ ...DEFAULT_HERO, ...h });
      } catch (err) {
        console.warn("[Reservation] Failed to load hero content — using defaults", err);
      }
    })();
    return () => { cancelled = true; };
  }, []);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  const inputCls =
    "w-full bg-transparent border-b border-border rounded-none px-0 py-3 text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:border-primary transition-colors duration-300 font-sans";

  const canSubmit =
    name && email && phone && date && time && party && !submitting;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!canSubmit) {
      toast.error("Please complete all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name,
        email,
        phone,
        date: format(date, "yyyy-MM-dd"),
        time,
        party_size: parseInt(party, 10),
        occasion: occasion && occasion !== "None" ? occasion : null,
        notes: notes || null,
      };
      const res = await createReservation(payload);
      setConfirmed(res);
      toast.success("Reservation received. We'll be in touch shortly.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please check your details and try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div data-testid="page-reservation" {...useSwipeToNavigate()} className="touch-pan-y">
      {/* Header */}
      <section className="relative border-b border-border min-h-[70vh] flex items-center">
        <HeroSlideshow page="reservation" />
        <div className="relative z-10 w-full mx-auto max-w-[1400px] px-6 lg:px-12 py-32 lg:py-40 fade-up">
          <div className="overline">{hero.overline || "Reservation"}</div>
          {hero.title_visible !== false && (
            <h1
              data-testid="reservation-hero-title"
              className="mt-8 serif text-5xl md:text-7xl lg:text-8xl font-light leading-[0.95] tracking-tighter text-foreground [text-shadow:0_2px_20px_rgba(0,0,0,0.55)]"
            >
              {hero.title_top || "A table,"} <br />
              <span className="italic text-foreground">{hero.title_italic || "held for you."}</span>
            </h1>
          )}
          {hero.body_visible !== false && (
            <p
              data-testid="reservation-hero-body"
              className="mt-8 max-w-xl text-sm md:text-base leading-relaxed text-foreground/90 [text-shadow:0_1px_10px_rgba(0,0,0,0.5)] whitespace-pre-line"
            >
              {hero.body ||
                "Dinner service runs Tuesday to Sunday from 18:00. Weekend service books out quickly — we recommend a fortnight ahead."}
            </p>
          )}
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-6 lg:px-12 py-24 lg:py-32 grid lg:grid-cols-12 gap-14 lg:gap-20">
        <div className="lg:col-span-4 space-y-10">
          <div>
            <div className="overline">{hero.reservation_notes_label}</div>
            <p className="mt-6 text-sm text-muted-foreground leading-relaxed whitespace-pre-line">
              {hero.reservation_notes_body}
            </p>
          </div>
          <div>
            <div className="overline">{hero.reservation_dress_label}</div>
            <p className="mt-6 text-sm text-muted-foreground leading-relaxed whitespace-pre-line">
              {hero.reservation_dress_body}
            </p>
          </div>
          <div>
            <div className="overline">{hero.reservation_enquiries_label}</div>
            <a
              href={`tel:${hero.reservation_phone.replace(/[^\d+]/g, "")}`}
              data-testid="reservation-phone"
              className="mt-6 block serif text-xl text-foreground hover:text-primary transition-colors"
            >
              {hero.reservation_phone}
            </a>
          </div>
        </div>

        <div className="lg:col-span-8">
          {confirmed ? (
            <div
              data-testid="reservation-confirmation"
              className="border border-primary/50 bg-card p-10 fade-up"
            >
              <div className="w-12 h-12 border border-primary flex items-center justify-center text-primary">
                <Check size={20} strokeWidth={1.5} />
              </div>
              <div className="overline mt-6">Confirmed</div>
              <h2 className="mt-4 serif text-4xl md:text-5xl font-light text-foreground leading-tight">
                Thank you, {confirmed.name.split(" ")[0]}.
              </h2>
              <p className="mt-6 text-sm text-muted-foreground leading-relaxed max-w-lg">
                We've received your booking for{" "}
                <span className="text-foreground">
                  {format(new Date(confirmed.date + "T00:00:00"), "EEEE, d MMMM yyyy")} at {confirmed.time}
                </span>{" "}
                for a party of {confirmed.party_size}. A confirmation will be sent to{" "}
                <span className="text-foreground">{confirmed.email}</span>.
              </p>
              <div className="mt-8 pt-8 border-t border-border grid grid-cols-2 sm:grid-cols-4 gap-6 text-sm">
                <div>
                  <div className="overline">Ref</div>
                  <div className="mt-2 text-foreground tabular-nums">
                    {confirmed.id.slice(0, 8).toUpperCase()}
                  </div>
                </div>
                <div>
                  <div className="overline">Date</div>
                  <div className="mt-2 text-foreground">
                    {format(new Date(confirmed.date + "T00:00:00"), "d MMM")}
                  </div>
                </div>
                <div>
                  <div className="overline">Time</div>
                  <div className="mt-2 text-foreground">{confirmed.time}</div>
                </div>
                <div>
                  <div className="overline">Guests</div>
                  <div className="mt-2 text-foreground">{confirmed.party_size}</div>
                </div>
              </div>
              <button
                data-testid="reservation-new-btn"
                onClick={() => {
                  setConfirmed(null);
                  setDate(undefined);
                  setTime("");
                  setParty("");
                  setOccasion("");
                  setName("");
                  setEmail("");
                  setPhone("");
                  setNotes("");
                }}
                className="mt-10 inline-flex items-center gap-3 px-7 py-4 border border-primary text-primary text-[0.72rem] uppercase tracking-[0.24em] font-semibold hover:bg-primary hover:text-primary-foreground transition-colors"
              >
                Make another
              </button>
            </div>
          ) : (
            <form
              data-testid="reservation-form"
              onSubmit={handleSubmit}
              className="space-y-12"
            >
              {/* Row 1 — Date / Time / Party */}
              <div className="grid md:grid-cols-3 gap-10">
                <div>
                  <label className="overline block mb-4">Date</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        data-testid="reservation-date-trigger"
                        className={cn(
                          "w-full flex items-center justify-between bg-transparent border-b border-border py-3 text-left font-sans transition-colors focus:outline-none focus:border-primary",
                          date ? "text-foreground" : "text-muted-foreground/70"
                        )}
                      >
                        {date ? format(date, "EEE, d MMM yyyy") : "Select a date"}
                        <CalendarIcon size={16} strokeWidth={1.5} className="text-primary" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent
                      align="start"
                      className="w-auto p-0 bg-card border border-border"
                    >
                      <Calendar
                        mode="single"
                        selected={date}
                        onSelect={setDate}
                        disabled={(d) => d < new Date(new Date().setHours(0,0,0,0)) || d.getDay() === 1}
                        initialFocus
                        data-testid="reservation-calendar"
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div>
                  <label className="overline block mb-4">Time</label>
                  <Select value={time} onValueChange={setTime}>
                    <SelectTrigger
                      data-testid="reservation-time-trigger"
                      className="w-full bg-transparent border-x-0 border-t-0 border-b border-border rounded-none px-0 py-3 h-auto text-foreground focus:ring-0 focus:border-primary hover:border-primary/60"
                    >
                      <SelectValue placeholder="Select a time" />
                    </SelectTrigger>
                    <SelectContent className="bg-card border border-border">
                      {TIMES.map((t) => (
                        <SelectItem key={t} value={t} data-testid={`reservation-time-${t}`}>
                          {t}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="overline block mb-4">Party</label>
                  <Select value={party} onValueChange={setParty}>
                    <SelectTrigger
                      data-testid="reservation-party-trigger"
                      className="w-full bg-transparent border-x-0 border-t-0 border-b border-border rounded-none px-0 py-3 h-auto text-foreground focus:ring-0 focus:border-primary hover:border-primary/60"
                    >
                      <SelectValue placeholder="Guests" />
                    </SelectTrigger>
                    <SelectContent className="bg-card border border-border">
                      {PARTY.map((p) => (
                        <SelectItem key={p} value={p} data-testid={`reservation-party-${p}`}>
                          {p} {p === "1" ? "guest" : "guests"}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Row 2 — Contact */}
              <div className="grid md:grid-cols-2 gap-10">
                <div>
                  <label className="overline block mb-4">Full Name</label>
                  <input
                    data-testid="reservation-name-input"
                    className={inputCls}
                    placeholder="e.g. Ayesha Fernando"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label className="overline block mb-4">Email</label>
                  <input
                    data-testid="reservation-email-input"
                    type="email"
                    className={inputCls}
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label className="overline block mb-4">Phone</label>
                  <input
                    data-testid="reservation-phone-input"
                    className={inputCls}
                    placeholder="+62 812 3456 7890"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label className="overline block mb-4">Occasion (optional)</label>
                  <Select value={occasion} onValueChange={setOccasion}>
                    <SelectTrigger
                      data-testid="reservation-occasion-trigger"
                      className="w-full bg-transparent border-x-0 border-t-0 border-b border-border rounded-none px-0 py-3 h-auto text-foreground focus:ring-0 focus:border-primary hover:border-primary/60"
                    >
                      <SelectValue placeholder="Any special occasion?" />
                    </SelectTrigger>
                    <SelectContent className="bg-card border border-border">
                      {OCCASIONS.map((o) => (
                        <SelectItem key={o} value={o} data-testid={`reservation-occasion-${o.toLowerCase().replace(/\s+/g, "-")}`}>
                          {o}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="overline block mb-4">Special Requests</label>
                <textarea
                  data-testid="reservation-notes-input"
                  className={inputCls + " resize-none"}
                  rows={3}
                  placeholder="Allergies, seating preference, dietary needs…"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </div>

              <div className="pt-4 flex flex-wrap items-center gap-6">
                <button
                  data-testid="reservation-submit-btn"
                  type="submit"
                  disabled={!canSubmit}
                  className="inline-flex items-center gap-3 px-8 py-4 bg-primary text-primary-foreground text-[0.72rem] uppercase tracking-[0.24em] font-semibold hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {submitting ? "Sending…" : "Confirm Reservation"}
                </button>
                <span className="text-xs text-muted-foreground italic">
                  We'll respond by email within 24 hours.
                </span>
              </div>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
